import { createContext, useState, useEffect, useRef } from "react";
import axiosInstance from "../axiosInstance";
// ⚠️ Pas de purge globale ici : on laisse les pages purger localement si besoin
// import { revokeAllProtectedUrls } from "../utils/protectedFiles";

export const SocieteContext = createContext();

/* ---------------- helpers auth ---------------- */
function getAuthHeader() {
  const a =
    axiosInstance?.defaults?.headers?.common?.Authorization ||
    axiosInstance?.defaults?.headers?.Authorization ||
    "";
  if (a) return a;

  try {
    const t =
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("jwt");

    // même logique qu’avant, juste corrigé en template string
    return t ? `Bearer ${t}` : "";
  } catch {
    return "";
  }
}

function hasAuth() {
  const hdr =
    axiosInstance?.defaults?.headers?.common?.Authorization ||
    getAuthHeader() ||
    "";
  return !!hdr;
}

// Signature courte (non sensible) pour détecter le changement d'auth
function sigAuth(h = "") {
  const s = String(h || "");
  return s ? s.slice(0, 24) : "anon";
}

/* ---------------- provider ---------------- */
export const SocieteProvider = ({ children }) => {
  const [infosSociete, setInfosSociete] = useState({});
  const [loading, setLoading] = useState(false); // on ne masque pas l'UI
  const lastAuthSigRef = useRef(sigAuth(getAuthHeader()));

  async function fetchEntreprise() {
    // Pas de token → pas d'appel → état invité
    if (!hasAuth()) {
      setInfosSociete({});
      setLoading(false);
      return;
    }

    try {
      const res = await axiosInstance.get("/entreprise");
      setInfosSociete(res.data || {});
    } catch (err) {
      console.error("❌ Erreur chargement infos société :", err);
      setInfosSociete({});
    } finally {
      setLoading(false);
    }
  }

  // 1) Chargement initial
  useEffect(() => {
    fetchEntreprise();
  }, []);

  // 2) Détecte le changement d'auth :
  //   - event 'storage' (autres onglets)
  //   - event custom 'auth-changed' (même onglet)
  //   - petit polling doux (si le header axios change sans event)
  useEffect(() => {
    const checkAuthChanged = async () => {
      const newSig = sigAuth(
        axiosInstance?.defaults?.headers?.common?.Authorization || getAuthHeader()
      );
      if (newSig !== lastAuthSigRef.current) {
        lastAuthSigRef.current = newSig;
        // Pas de reset visuel agressif → évite le flicker
        await fetchEntreprise();
      }
    };

    const onStorage = () => checkAuthChanged();
    const onAuthChanged = () => checkAuthChanged();

    window.addEventListener("storage", onStorage);
    window.addEventListener("auth-changed", onAuthChanged);

    // Polling léger (pour changements silencieux du header axios dans le même onglet)
    const iv = setInterval(checkAuthChanged, 1500);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth-changed", onAuthChanged);
      clearInterval(iv);
    };
  }, []);

  // 3) méthode manuelle au cas où
  const reloadSociete = async () => {
    await fetchEntreprise();
  };

  return (
    <SocieteContext.Provider
      value={{ infosSociete, setInfosSociete, loading, reloadSociete }}
    >
      {/* Toujours rendre les enfants : pas de remount/flicker */}
      {children}
    </SocieteContext.Provider>
  );
};
